import { Component, Input, Output, EventEmitter, OnInit, ViewEncapsulation, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { ToastService } from 'src/app/services/toast.service';

interface SelectItem {
  name: string;
  value: string;
}

interface WeekDay {
  value: string;
  label: string;
  open: string;
  close: string;
  closed: boolean;
}

@Component({
  selector: 'app-hub-selection',
  templateUrl: './hub-selection.page.html',
  styleUrls: ['./hub-selection.page.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ]
})
export class HubSelectionPage implements OnInit {
  @ViewChild('logoInput') logoInput!: ElementRef<HTMLInputElement>;

  @Input() joinForm: FormGroup | undefined;
  @Input() hubForm: FormGroup | undefined;
  @Output() selectionChange = new EventEmitter<string>();
  @Output() openingHoursChange = new EventEmitter<any>();

  selectedOption: 'join' | 'create' | null = null;

  // Hub Avatar
  hubAvatarFile: File | null = null;
  hubAvatarPreview: string | null = null;
  isUploadingHubAvatar = false;
  hubAvatarUploadProgress = 0;

  hubTypes: SelectItem[] = [
    { value: 'gemeinde', name: 'Gemeinde' },
    { value: 'stadt', name: 'Stadt' },
    { value: 'landkreis', name: 'Landkreis' },
    { value: 'quartier', name: 'Quartier' }
  ];

  weekDays: WeekDay[] = [
    { value: 'monday', label: 'Mo', open: '08:00', close: '17:00', closed: false },
    { value: 'tuesday', label: 'Di', open: '08:00', close: '17:00', closed: false },
    { value: 'wednesday', label: 'Mi', open: '08:00', close: '17:00', closed: false },
    { value: 'thursday', label: 'Do', open: '08:00', close: '17:00', closed: false },
    { value: 'friday', label: 'Fr', open: '08:00', close: '17:00', closed: false },
    { value: 'saturday', label: 'Sa', open: '', close: '', closed: true },
    { value: 'sunday', label: 'So', open: '', close: '', closed: true }
  ];

  constructor(private _toast: ToastService) {}

  ngOnInit() {

  }

  selectOption(option: 'join' | 'create') {
    this.selectedOption = option;
    this.selectionChange.emit(option);
  }

  // ==================== HUB TYPE HANDLING ====================
  
  /**
   * Called when hub type is selected from dropdown
   * Ensures the value is properly set in the form
   */
  onHubTypeChange(event: any) {
    if (this.hubForm) {
      // PrimeNG Select returns the full object or just the value depending on setup
      const typeValue = typeof event === 'string' ? event : event?.value;
      this.hubForm.patchValue({ type: typeValue });
    }
  }

  /**
   * Get current selected hub type for display
   */
  getSelectedHubType(): SelectItem | undefined {
    if (!this.hubForm) return undefined;
    const currentType = this.hubForm.get('type')?.value;
    return this.hubTypes.find(t => t.value === currentType);
  }

  // ==================== HUB AVATAR ====================

  triggerLogoUpload() {
    this.logoInput?.nativeElement?.click();
  }

  onLogoSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) return;

    this.hubAvatarFile = file;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.hubAvatarPreview = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  async onHubAvatarSelect(file: File) {
    this.hubAvatarFile = file;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.hubAvatarPreview = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  removeHubAvatar() {
    this.hubAvatarFile = null;
    this.hubAvatarPreview = null;
    if (this.hubForm) {
      this.hubForm.patchValue({ avatar: '' });
    }
  }

  async uploadHubAvatar(): Promise<string | null> {
    if (!this.hubAvatarFile) return null;

    this.isUploadingHubAvatar = true;
    this.hubAvatarUploadProgress = 0;

    try {
      const storage = getStorage();
      const user = getAuth().currentUser;
      if (!user) throw new Error('User not authenticated');

      const storageRef = ref(
        storage,
        `avatars/regional/${user.uid}/hub/${Date.now()}_${this.hubAvatarFile.name}`
      );

      return new Promise<string>((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, this.hubAvatarFile!);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            this.hubAvatarUploadProgress = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
          },
          (error) => {
            this.isUploadingHubAvatar = false;
            this._toast.add({
              severity: 'error',
              summary: 'Upload Fehler',
              detail: 'Avatar konnte nicht hochgeladen werden'
            });
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(storageRef);
            this.isUploadingHubAvatar = false;
            resolve(downloadURL);
          }
        );
      });
    } catch (error) {
      this.isUploadingHubAvatar = false;
      throw error;
    }
  }

  // Method to be called from parent before creating hub
  async getHubAvatarUrl(): Promise<string | null> {
    if (this.hubAvatarFile) {
      return await this.uploadHubAvatar();
    }
    return null;
  }

  // ==================== OPENING HOURS ====================

  getOpeningHoursData() {
    if (this.hubForm?.get('is_24_7')?.value) {
      return null;
    }
    const openingHours: any = {
      regular: {}
    };
    this.weekDays.forEach(day => {
      openingHours.regular[day.value] = {
        open: day.open || '',
        close: day.close || '',
        closed: day.closed
      };
    });
    return openingHours;
  }

  // ==================== VALIDATION ====================

  /**
   * Validate that hub type is selected before submission
   */
  isHubTypeValid(): boolean {
    if (!this.hubForm) return false;
    const typeValue = this.hubForm.get('type')?.value;
    return !!typeValue && this.hubTypes.some(t => t.value === typeValue);
  }
}